import styled from '@emotion/styled';

import { DataGrid } from 'devextreme-react/data-grid';
import 'devextreme/dist/css/dx.light.css';

import { TInternalProps } from 'types/gridDefs';

/* ======================================== */

/**
 * Base styled implementation of the DevExpress / DevExtreme DataGrid
 * Styled to override standard devExtreme grid (dx.light.css) with common adserve grid styling
 * @private
 * !! Do not export from the library !!
  */
export const StyledDataGrid = styled(DataGrid)<TInternalProps>(
    ({ compact }) => ({
        // theme not yet configured - using static styling here to match existing adserve grids - TODO incorporate a theme / styling library
        '&.dx-widget .dx-datagrid': {
            backgroundColor: 'transparent',
            fontFamily: 'Roboto, Arial, sans-serif',
            userSelect: 'none',

            '& div.dx-datagrid-headers': {
                borderWidth: 0,
                backgroundColor: 'transparent'
            },
            '& div.dx-datagrid-rowsview': {
                borderWidth: 0,
                backgroundColor: 'transparent',
                marginTop: 1
            },

            // header row
            '& tr.dx-header-row': {
                '& > td, & > td:first-of-type': {
                    position: 'relative',
                    fontSize: compact ? 14 : 15,
                    fontWeight: 800,
                    height: 37,
                    lineHeight: compact ? '16px' : '18px',
                    verticalAlign: 'middle',
                    color: '#414141',
                    backgroundColor: '#FFFFFF',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: '#E3E3E3',
                    zIndex: 1,

                    '& > div.dx-datagrid-text-content': {    // centre-align column headers (default would be to match column content)
                        width: '100%',
                        maxWidth: '100%',
                        margin: 0,
                        padding: '1px 12px',
                        textAlign: 'center',
                        whiteSpace: 'normal'
                    },
                    '& > div.dx-column-indicators': {       // change sort indicators to chevron (rather than tall/thin arrow)
                        position: 'absolute',
                        top: '50%',
                        right: 2,
                        transform: 'translateY(-50%)'
                    },
                    '&.dx-datagrid-group-space': {
                        borderRightColor: 'transparent'
                    },
                    '&.dx-disabled': {
                        color: '#41414155'  // TODO theme
                    }
                }
            },

            // group / summary / totals rows
            '& tr.dx-group-row, & tr.dx-footer-row': {
                '& > td': {
                    fontSize: compact ? 14 : 15,
                    fontWeight: 800,
                    height: compact ? 30 : 40,
                    lineHeight: compact ? '16px' : '18px',
                    verticalAlign: 'middle',
                    paddingLeft: 10,
                    color: '#414141',
                    backgroundColor: '#EAEAEA',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: '#D7D7D7',
                    width: 'unset!important',       // allow header cell to auto-size, otherwise breaks alignment of regular columns/rows
                    minWidth: 'unset!important',
                    maxWidth: 'unset!important'
                },
                '& > td.dx-command-expand': {
                    padding: 0,
                    borderColor: '#EAEAEA',
                    borderBottomColor: '#D7D7D7',
                    '& > div': {
                        marginLeft: 6
                    }
                },
                '&:has(.as-hidden)': {
                    display: 'none'
                }
            },

            '& tr.dx-data-row:first-of-type, & tr.dx-data-row:first-of-type > td': {
                borderTopWidth: '0!important'
            },

            // data row
            '& tr.dx-data-row': {
                '& > td, & > td:first-of-type': {
                    fontSize: compact ? 14 : 15,
                    fontWeight: 500,
                    height: compact ? 30 : 40,
                    lineHeight: compact ? '16px' : '18px',
                    verticalAlign: 'middle',
                    paddingLeft: 10,
                    color: '#414141',
                    backgroundColor: '#F7F7F7',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: '#D7D7D7',

                    '&.dx-editor-cell .dx-textbox, &.dx-editor-cell .dx-texteditor-container': {
                        margin: '2px 2px 2px -3px',
                        'input': {
                            fontFamily: 'Roboto Medium, Arial, sans-serif',
                            fontSize: compact ? 14 : 15,
                            padding: 6,
                            minHeight: 30
                        }
                    },
                    '&.dx-datagrid-group-space': {
                        borderRightColor: 'transparent'
                    },
                    '&.dx-disabled': {
                        color: '#41414155'  // TODO theme
                    },
                    '&.dx-actions': {
                        padding: 0,
                        
                    }
                },

                '&.dx-row-alt > td': {
                    backgroundColor: '#FFFFFF'
                },
                '&.dx-state-hover:not(.dx-selection):not(.dx-row-inserted):not(.dx-row-removed):not(.dx-edit-row):not(.dx-row-focused) > td:not(.dx-focused)': {
                    color: '#414141',
                    backgroundColor: '#FFFDF4',
                    '&.dx-disabled': {
                        color: '#41414155'
                    }
                },
                '&.dx-selection:not(.dx-row-focused) > td, &.dx-edit-row > td': {
                    color: '#414141',
                    backgroundColor: '#FDF1D6',
                    '&.dx-disabled': {
                        color: '#41414155'
                    }
                }
            },

            // total row - fixed to the bottom of the grid
            '& .dx-datagrid-total-footer': {
                borderTopWidth: 0,
                '& > .dx-datagrid-content': {
                    padding: 0,
                    '& td': {
                        borderTopWidth: 0
                    }
                }
            },

            // loading overlay
            '& .dx-overlay-wrapper.dx-loadpanel-wrapper': {
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(255,255,255,0.4)',
                pointerEvents: 'none',
                '& .dx-loadpanel-content': {
                    left: '50%!important',
                    top: '40%!important',
                    minWidth: 200,
                    maxWidth: 400,
                    width: 'auto!important',
                    height: 'auto!important',
                    transform: 'translate(-50%,-50%)!important'
                }
            },

            // outline content area when "no data" shown
            '& .dx-scrollable-container': {
                boxShadow: 'inset 0 0 1px black'
            },
            // padding bottom of grid when not filled
            '& .dx-freespace-row': {
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: '#D7D7D7'
            }
        }
    })
);
